<div class="flex flex-col gap-4">
  @if (isLoading()) {
    <div class="flex flex-col gap-3" aria-busy="true">
      <p-skeleton width="18rem" height="0.875rem" />
      <div class="flex flex-col gap-2 p-3 border border-surface-100 rounded-md">
        <p-skeleton width="12rem" height="1rem" />
        <p-skeleton width="16rem" height="0.875rem" />
      </div>
      <p-skeleton width="10rem" height="0.875rem" />
    </div>
  } @else if (hasError()) {
    <div
      class="flex items-center gap-2 py-2 px-3 rounded-md bg-surface-50 text-body-sm-500 text-surface-600"
      role="status"
    >
      <i class="pi pi-exclamation-triangle text-amber-600 text-body-sm"></i>
      <span>Couldn't load what the rules would change.</span>
      <button
        type="button"
        class="text-body-sm-500 text-surface-600 hover:text-surface-800 underline bg-transparent border-none p-0 cursor-pointer"
        (click)="retry()"
      >
        Retry
      </button>
    </div>
  } @else if (!hasRulesToRun()) {
    <div
      class="flex items-start gap-2 py-2 px-3 rounded-md bg-surface-50 text-body-sm-500 text-surface-600"
      role="status"
    >
      <i class="pi pi-check-circle text-emerald-600 text-body-sm mt-0.5"></i>
      <span>
        Nothing to run. Every location rule {{ addendumLabel() }} triggers has already run on this home, or none apply
        to its model and elevation.
      </span>
    </div>
  } @else {
    <p class="m-0 text-body-sm text-surface-600">
      Running the rules on {{ addendumLabel() }} makes these changes, exactly as locking it would have.
    </p>

    <section class="flex flex-col gap-2" aria-label="Location changes">
      <div class="text-body-xs-500 text-surface-500 uppercase tracking-wide">Location changes</div>
      <ul class="flex flex-col m-0 p-0 list-none border border-surface-100 rounded-md overflow-hidden">
        @for (rule of ruleRows(); track rule.ruleId) {
          <li class="flex flex-col gap-1 py-2 px-3 border-0 border-b border-surface-100 last:border-b-0">
            <span class="text-body-sm-600 text-surface-800">{{ rule.trigger }}</span>
            <span class="text-body-sm text-surface-600">
              {{ rule.replacedRooms }}
              <i class="pi pi-arrow-right text-body-xs text-surface-400 mx-1" aria-label="replaced by"></i>
              {{ rule.newRooms }}
            </span>
          </li>
        }
      </ul>
    </section>

    <section class="flex flex-col gap-2" aria-label="Options cancelled">
      <div class="text-body-xs-500 text-surface-500 uppercase tracking-wide">Options cancelled</div>
      @if (cancelledCount() === 0) {
        <p class="m-0 text-body-sm text-surface-600">None. No locked options sit in the locations being replaced.</p>
      } @else {
        <p class="m-0 text-body-sm text-red-700">
          These {{ cancelledCountLabel() }} on locked addendums sit in locations being replaced and will be cancelled:
        </p>
        <ul class="flex flex-col m-0 p-0 list-none border border-red-100 rounded-md overflow-hidden">
          @for (line of cancelledLineRows(); track line.addendumProductId) {
            <li
              class="flex items-center justify-between gap-3 py-1.5 px-3 border-0 border-b border-red-100 last:border-b-0"
            >
              <span class="text-body-sm-500 text-surface-800 truncate">{{ line.productCode }}</span>
              <span class="text-body-sm text-surface-500 shrink-0"
                >{{ line.locationName }} · {{ line.addendumLabel }}</span
              >
            </li>
          }
        </ul>
      }
    </section>

    <label class="flex items-center gap-2 cursor-pointer" for="dsm-room-replacement-rerun-notify">
      <p-checkbox
        inputId="dsm-room-replacement-rerun-notify"
        [binary]="true"
        [ngModel]="notifyConsultants()"
        (onChange)="notifyConsultants.set(!!$event.checked)"
      />
      <span class="text-body-sm text-surface-700">Notify the deal's consultants</span>
    </label>

    @if (isCancellingSilently()) {
      <div class="flex items-start gap-2 py-2 px-3 rounded-md bg-amber-50 text-body-sm text-amber-800" role="alert">
        <i class="pi pi-exclamation-triangle text-body-sm mt-0.5"></i>
        <span>{{ cancelledCountLabel() }} will be cancelled without telling the consultants.</span>
      </div>
    }
  }

  <div class="flex justify-end gap-2 pt-2">
    <p-button label="Close" severity="secondary" type="button" (onClick)="close()" />
    @if (hasRulesToRun()) {
      <p-button
        label="Run rules"
        type="button"
        [severity]="runSeverity()"
        [disabled]="isRunDisabled()"
        [loading]="isRunning()"
        (onClick)="run()"
      />
    }
  </div>
</div>
